import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import EcoIcon, { EcoIconName } from "../../../components/eco-icon/EcoIcon";
import EcoLogoWhite from "../../../img/eco/logo-white.png";

import "./Nr1PublicFormPage.css";
import {
  getNr1PublicFormApi,
  submitNr1ResponseApi,
  type Nr1Answer,
  type Nr1PublicError,
  type Nr1PublicForm,
  type Nr1PublicQuestion,
} from "../nr1Api";

type PageStatus = "loading" | "form" | "submitting" | "success" | "gone" | "notFound" | "error";

type AnswerState = {
  booleanValue?: boolean;
  intValue?: number;
  textValue?: string;
  dateValue?: string;
  optionId?: string;
  optionIds?: string[];
};

/**
 * Pesquisa NR1 aberta pelo link de uso único — sem login, sem menu. Mobile
 * primeiro: quase todo mundo responde pelo celular. A lógica (validação de
 * obrigatórias, montagem do envio, estados do link) é a mesma de antes; o que
 * mudou é a aparência.
 */
const Nr1PublicFormPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [status, setStatus] = useState<PageStatus>("loading");
  const [statusMessage, setStatusMessage] = useState("");
  const [data, setData] = useState<Nr1PublicForm | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [invalidQuestionIds, setInvalidQuestionIds] = useState<Set<string>>(new Set());
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("notFound");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await getNr1PublicFormApi(token);
        if (cancelled) return;
        setData(result);
        setStatus("form");
      } catch (e) {
        if (cancelled) return;
        const err = e as Nr1PublicError;
        setStatusMessage(err.message ?? "");
        if (err.kind === "GONE") setStatus("gone");
        else if (err.kind === "NOT_FOUND") setStatus("notFound");
        else setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const questions = useMemo(
    () => (data?.form?.questions ?? []).slice().sort((a, b) => a.order - b.order),
    [data]
  );

  const setAnswer = (questionId: string, value: AnswerState) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setInvalidQuestionIds((prev) => {
      if (!prev.has(questionId)) return prev;
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  };

  const hasValue = (q: Nr1PublicQuestion, a?: AnswerState): boolean => {
    if (!a) return false;
    if (q.type === "BOOLEAN") return a.booleanValue != null;
    if (q.type === "SCALE") return a.intValue != null;
    if (q.type === "TEXT") return !!a.textValue?.trim();
    if (q.type === "DATE") return !!a.dateValue;
    if (q.type === "SINGLE_CHOICE") return !!a.optionId;
    if (q.type === "MULTI_CHOICE") return !!a.optionIds?.length;
    return false;
  };

  const answeredCount = questions.filter((q) => hasValue(q, answers[q.id])).length;

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!token) return;

    const missing = questions.filter((q) => q.required && !hasValue(q, answers[q.id]));
    if (missing.length) {
      setInvalidQuestionIds(new Set(missing.map((q) => q.id)));
      setSubmitError("Responda as perguntas obrigatórias destacadas antes de enviar.");
      const firstId = missing[0].id;
      document.getElementById(`nr1-q-${firstId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const payload: Nr1Answer[] = questions
      .filter((q) => hasValue(q, answers[q.id]))
      .map((q) => {
        const a = answers[q.id];
        return {
          questionId: q.id,
          booleanValue: q.type === "BOOLEAN" ? a.booleanValue : undefined,
          intValue: q.type === "SCALE" ? a.intValue : undefined,
          textValue: q.type === "TEXT" ? a.textValue?.trim() : undefined,
          dateValue: q.type === "DATE" ? a.dateValue : undefined,
          optionId: q.type === "SINGLE_CHOICE" ? a.optionId : undefined,
          optionIds: q.type === "MULTI_CHOICE" ? a.optionIds : undefined,
        };
      });

    if (!payload.length) {
      setSubmitError("Responda ao menos uma pergunta antes de enviar.");
      return;
    }

    setSubmitError("");
    setStatus("submitting");

    try {
      await submitNr1ResponseApi(token, payload);
      setStatus("success");
      window.scrollTo({ top: 0 });
    } catch (e) {
      const err = e as Nr1PublicError;
      if (err.kind === "GONE") {
        setStatusMessage(err.message ?? "");
        setStatus("gone");
      } else {
        setStatus("form");
        setSubmitError(err.message || "Erro ao enviar. Tente novamente.");
      }
    }
  };

  const total = questions.length;

  const renderQuestion = (q: Nr1PublicQuestion, index: number) => {
    const a = answers[q.id] ?? {};
    const invalid = invalidQuestionIds.has(q.id);
    const counter = `Pergunta ${index + 1} de ${total}`;
    const promptId = `nr1-p-${q.id}`;
    const busy = status === "submitting";

    const prompt = (
      <p className="nr1Pub__prompt" id={promptId}>
        {q.prompt}
        {q.required ? (
          <span className="nr1Pub__required">
            {" "}*<span className="nr1Pub__sr"> (obrigatória)</span>
          </span>
        ) : (
          <span className="nr1Pub__optional"> (opcional)</span>
        )}
      </p>
    );

    return (
      <div
        key={q.id}
        id={`nr1-q-${q.id}`}
        className={`nr1Pub__question${invalid ? " nr1Pub__question--invalid" : ""}`}
      >
        <span className="nr1Pub__counter">{counter}</span>
        {q.type === "TEXT" ? (
          <label htmlFor={`nr1-t-${q.id}`} className="nr1Pub__prompt">
            {q.prompt}
            {q.required ? (
              <span className="nr1Pub__required">
                {" "}*<span className="nr1Pub__sr"> (obrigatória)</span>
              </span>
            ) : (
              <span className="nr1Pub__optional"> (opcional)</span>
            )}
          </label>
        ) : (
          prompt
        )}

        {q.type === "SCALE" ? (
          <>
            <div className="nr1Pub__scale" role="radiogroup" aria-labelledby={promptId}>
              {(() => {
                const min = q.minValue ?? 1;
                const max = q.maxValue ?? 5;
                const values: number[] = [];
                for (let v = min; v <= max; v++) values.push(v);
                return values.map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={a.intValue === v}
                    className={`nr1Pub__opt${a.intValue === v ? " is-selected" : ""}`}
                    onClick={() => setAnswer(q.id, { intValue: v })}
                    disabled={busy}
                  >
                    {v}
                  </button>
                ));
              })()}
            </div>
            <div className="nr1Pub__scaleLegend">
              <span>Discordo totalmente</span>
              <span>Concordo totalmente</span>
            </div>
          </>
        ) : null}

        {q.type === "BOOLEAN" ? (
          <div className="nr1Pub__yesNo" role="radiogroup" aria-labelledby={promptId}>
            {[
              { label: "Sim", value: true },
              { label: "Não", value: false },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                role="radio"
                aria-checked={a.booleanValue === opt.value}
                className={`nr1Pub__opt${a.booleanValue === opt.value ? " is-selected" : ""}`}
                onClick={() => setAnswer(q.id, { booleanValue: opt.value })}
                disabled={busy}
              >
                {opt.label}
              </button>
            ))}
          </div>
        ) : null}

        {q.type === "SINGLE_CHOICE" ? (
          <div className="nr1Pub__choices" role="radiogroup" aria-labelledby={promptId}>
            {q.options.map((opt) => (
              <label key={opt.id} className={`nr1Pub__choice${a.optionId === opt.id ? " is-selected" : ""}`}>
                <input
                  type="radio"
                  name={`q-${q.id}`}
                  checked={a.optionId === opt.id}
                  onChange={() => setAnswer(q.id, { optionId: opt.id })}
                  disabled={busy}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        ) : null}

        {q.type === "MULTI_CHOICE" ? (
          <div className="nr1Pub__choices" role="group" aria-labelledby={promptId}>
            {q.options.map((opt) => {
              const checked = a.optionIds?.includes(opt.id) ?? false;
              return (
                <label key={opt.id} className={`nr1Pub__choice${checked ? " is-selected" : ""}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const current = a.optionIds ?? [];
                      const next = checked ? current.filter((id) => id !== opt.id) : [...current, opt.id];
                      setAnswer(q.id, { optionIds: next });
                    }}
                    disabled={busy}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        ) : null}

        {q.type === "TEXT" ? (
          <textarea
            id={`nr1-t-${q.id}`}
            className="nr1Pub__textarea"
            rows={4}
            maxLength={2000}
            placeholder="Escreva aqui. Sua resposta é anônima."
            value={a.textValue ?? ""}
            onChange={(e) => setAnswer(q.id, { textValue: e.target.value })}
            disabled={busy}
          />
        ) : null}

        {q.type === "DATE" ? (
          <input
            className="nr1Pub__date"
            type="date"
            aria-labelledby={promptId}
            value={a.dateValue ?? ""}
            onChange={(e) => setAnswer(q.id, { dateValue: e.target.value })}
            disabled={busy}
          />
        ) : null}

        {invalid ? <span className="nr1Pub__invalidText">Esta pergunta é obrigatória.</span> : null}
      </div>
    );
  };

  // ---- Telas de estado (carregando, enviado, link encerrado, erro) ----------
  const renderStatus = (icon: EcoIconName, tone: "ok" | "neutral" | "warn", title: string, text: string, badge?: string) => (
    <div className="nr1Pub nr1Pub--status">
      <img className="nr1Pub__logo" src={EcoLogoWhite} alt="Eco System Serviços" />
      <div className="nr1Pub__statusBody" role="status">
        <span className={`nr1Pub__statusIcon nr1Pub__statusIcon--${tone}`} aria-hidden="true">
          <EcoIcon name={icon} size={44} strokeWidth={2.4} />
        </span>
        <h1 className="nr1Pub__statusTitle">{title}</h1>
        <p className="nr1Pub__statusText">{text}</p>
        {badge ? (
          <span className="nr1Pub__chip">
            <EcoIcon name="lock" size={14} strokeWidth={2.2} />
            {badge}
          </span>
        ) : null}
      </div>
      {status === "success" || status === "gone" ? <p className="nr1Pub__closeNote">Pode fechar esta página.</p> : null}
    </div>
  );

  if (status === "loading") {
    return renderStatus("lock", "neutral", "Carregando...", "Validando seu link, aguarde um instante.");
  }
  if (status === "notFound") {
    return renderStatus(
      "search",
      "warn",
      "Link não encontrado",
      statusMessage || "Este link não existe ou está incorreto. Confira o endereço recebido.",
    );
  }
  if (status === "gone") {
    return renderStatus("lock", "neutral", "Link indisponível", statusMessage || "Este link expirou ou já foi utilizado.", "Este link foi encerrado");
  }
  if (status === "error") {
    return renderStatus(
      "alertTriangle",
      "warn",
      "Algo deu errado",
      statusMessage || "Não foi possível carregar o formulário. Tente novamente mais tarde.",
    );
  }
  if (status === "success") {
    const storeName = data?.store.nomeFantasia;
    return renderStatus(
      "check",
      "ok",
      "Obrigado pela sua participação",
      `Sua resposta é anônima e vai ajudar a melhorar o ambiente de trabalho${storeName ? ` da ${storeName}` : ""}.`,
      "Este link foi encerrado",
    );
  }

  if (!data?.form) {
    return renderStatus("alertTriangle", "warn", "Algo deu errado", "Este link não tem um formulário associado.");
  }

  const expires = new Date(data.expiresAt);
  const progress = total > 0 ? answeredCount / total : 0;

  return (
    <div className="nr1Pub">
      <header className="nr1Pub__header">
        <div className="nr1Pub__headerInner">
          <img className="nr1Pub__logo" src={EcoLogoWhite} alt="Eco System Serviços" />
          <div className="nr1Pub__titles">
            <span className="nr1Pub__store">{data.store.nomeFantasia}</span>
            <h1 className="nr1Pub__title">{data.form.title}</h1>
            <p className="nr1Pub__subtitle">Sobre o seu dia a dia de trabalho.</p>
          </div>
          <div className="nr1Pub__chips">
            <span className="nr1Pub__chip">
              <EcoIcon name="lock" size={14} strokeWidth={2.2} />
              Resposta 100% anônima
            </span>
            {!Number.isNaN(expires.getTime()) ? (
              <span className="nr1Pub__chip">
                Responda até {expires.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <main className="nr1Pub__main">
        <p className="nr1Pub__privacy">
          Suas respostas não ficam ligadas a você nem ao link que você recebeu. Da resposta, só o dia em que ela foi
          enviada é guardado.
        </p>

        <form className="nr1Pub__form" onSubmit={handleSubmit} noValidate>
          {questions.map(renderQuestion)}

          <div className="nr1Pub__footer">
            <div className="nr1Pub__progress">
              <span className="nr1Pub__progressTrack" aria-hidden="true">
                <span className="nr1Pub__progressFill" style={{ width: `${progress * 100}%` }} />
              </span>
              <span aria-live="polite">
                {answeredCount} de {total} respondidas
              </span>
            </div>

            {submitError ? (
              <p className="nr1Pub__error" role="alert">
                {submitError}
              </p>
            ) : null}

            <button type="submit" className="nr1Pub__submit" disabled={status === "submitting"}>
              {status === "submitting" ? "Enviando..." : "Enviar respostas"}
            </button>
            <p className="nr1Pub__note">Depois de enviar, este link deixa de funcionar.</p>
          </div>
        </form>
      </main>
    </div>
  );
};

export default Nr1PublicFormPage;
